import { useEffect, useState } from 'react';
import { useParams } from 'react-router-dom';
import { useDispatch, useSelector } from 'react-redux';

import ProjectDetailsKanbanView from '@features/project-management-tools/components/ProjectDetailsKanbanView';
import FilterTask from '@features/project-management-tools/components/FilterTask';
import {
	useFetchTasksQuery,
	useGetGroupsQuery,
	useGetProjectTagsQuery
} from '@features/project-management-tools/api/projectManagementApi';
import ProjectDetailsTableView from '@features/project-management-tools/components/ProjectDetailsTableView';
import { sortWithUniqueOrder } from '@features/project-management-tools/utils';
import { setTaskList } from '@features/project-management-tools/redux/myProjectDetailsSlice';
import { MIX_PANEL_EVENTS, TASK_SECTION_TABS } from '@features/project-management-tools/constents';

import { useMyProjectDetails } from '@/store/reducers';
import FilterComponent from '@/features/filter/v2/FilterComponent';
import { allFilterPages, projectDetailsFilterFields } from '@/features/filter/static/filterUtil';
import { useMixPanelTrakingEventMutation } from '@/api/commonApis';
import FileUploads from '@/features/blockSuggestion/components/fileUpload/FileUploads';

const ProjectDetailsTaskSection = ({ refetchProjectDetails, projectDetails }) => {
	const [showFilter, setShowFilter] = useState(false);
	const { id } = useParams();
	const dispatch = useDispatch();
	const { groupList, taskList, originalTaskList } = useMyProjectDetails();

	const filterFields = useSelector((store) => store?.filterFieldStore)?.DETAILS_PAGE_FILTERS;

	const { data: projectTags, refetch: refetchProjectTags } = useGetProjectTagsQuery({
		params: { tagType: 'PROJECT_TASK_STATUS', sort: 'sl,asc' },
		projectId: id
	});

	const [mixPanelTrakingEvent] = useMixPanelTrakingEventMutation();
	const { refetch: refetchTasks } = useFetchTasksQuery({ projectId: id });
	const { refetch: refetchGroups } = useGetGroupsQuery({ projectId: id });

	const [groupedTasksForKanban, setGroupedTasksForKanban] = useState({});
	const [groupedTasksForTable, setGroupedTasksForTable] = useState({});
	const [activeTab, setActiveTab] = useState('TABLE_VIEW');

	useEffect(() => {
		if (Object.keys(filterFields).length > 0) {
			const filteredTaskList = originalTaskList?.filter((task) => {
				const subcategoryFilter = filterFields.filterSubCategoryList;
				const ownerFilter = filterFields.ownerList;

				let subcategoryMatch = true;
				let ownerMatch = true;

				if (subcategoryFilter && subcategoryFilter.length > 0) {
					subcategoryMatch = subcategoryFilter.some(
						(filter) => String(task.taskStatusTagResponse?.id) === String(filter.key)
					);
				}

				if (ownerFilter && ownerFilter.length > 0) {
					ownerMatch = ownerFilter.some(
						(filter) => String(task.ownerResponse?.id) === String(filter.key)
					);
				}

				return subcategoryMatch && ownerMatch;
			});
			dispatch(setTaskList(filteredTaskList));
		} else {
			refetchTasks();
		}
	}, [filterFields]);

	useEffect(() => {
		refetchTasks();
		refetchGroups();
	}, [id]);

	const handleGroupTasksForKanban = () => {
		// Get project task status tags
		const statusTags = projectTags || [];

		// Create initial structure with all available statuses
		const groupedTasks = statusTags.reduce((acc, statusTag) => {
			acc[statusTag.text] = {
				id: statusTag.id,
				text: statusTag.text,
				colorCode: statusTag.colorCode,
				tasks: [],
				status: statusTag.status,
				type: statusTag.type
			};
			return acc;
		}, {});

		taskList.forEach((task) => {
			const status = task?.taskStatusTagResponse?.text;
			if (status && groupedTasks[status]) {
				groupedTasks[status].tasks.push(task);
			} else if (status) {
				groupedTasks[status] = {
					id: task?.taskStatusTagResponse?.id,
					text: status,
					colorCode: task?.taskStatusTagResponse?.colorCode,
					tasks: [task],
					status: task?.taskStatusTagResponse?.status,
					type: task?.taskStatusTagResponse?.type
				};
			}
		});

		// Sort each group by sl
		// Object.keys(groupedTasks).forEach((status) => {
		// 	groupedTasks[status].tasks = sortWithUniqueOrder(groupedTasks[status].tasks);
		// });

		setGroupedTasksForKanban(groupedTasks);
	};

	const handleGroupTasksForTable = (groupList, taskList) => {
		const groupBy = {};

		groupList.forEach((group) => {
			groupBy[group.name] = {
				sl: group?.sl,
				groupData: [],
				groupId: group?.id,
				colorCode: group?.colorCode,
				groupName: group?.name
			};
		});

		taskList.forEach((task) => {
			const groupEntry = Object.entries(groupBy).find(
				([, value]) => parseInt(value.groupId) === parseInt(task.taskGroupResponse?.id)
			);
			if (groupEntry) {
				groupEntry[1].groupData.push(task);
			}
		});

		// Sort groupData in each group by sl
		Object.keys(groupBy).forEach((groupName) => {
			groupBy[groupName].groupData = sortWithUniqueOrder(groupBy[groupName].groupData);
		});

		setGroupedTasksForTable(groupBy);
	};

	useEffect(() => {
		if (groupList || taskList) {
			handleGroupTasksForKanban();
			handleGroupTasksForTable(groupList, taskList);
		}
	}, [groupList, taskList]);

	return (
		<div className="task-table-and-kanban-container bg-white">
			<div className="navigation-tab bg-white px-0">
				<ul>
					{TASK_SECTION_TABS.map((tab) => (
						<li
							className={activeTab === tab.id ? 'active' : ''}
							onClick={() => {
								mixPanelTrakingEvent({
									type: MIX_PANEL_EVENTS.PROJECT_TASK_LIST_VIEW_CHANGE,
									body: {
										tabName: tab?.id
									}
								});
								setActiveTab(tab.id);
							}}
						>
							{tab?.name}
						</li>
					))}
				</ul>
			</div>
			{activeTab !== 'FILES_VIEW' && (
				<FilterTask id={id} setShowFilter={setShowFilter} showFilter={showFilter} />
			)}
			<section className="bg-white filter-data-container filter-v3-data-container bg-white p-0">
				<FilterComponent
					filterPageName={allFilterPages.PROJECT_DETAILS}
					fields={projectDetailsFilterFields}
					showFilter={showFilter}
					isDependentFilter={true}
					className="favourite-details-filter-bar"
				/>
				<div className={`data-table ${activeTab === 'KANBAN_VIEW' ? 'scroll-x-label' : ''}`}>
					{activeTab === 'TABLE_VIEW' && (
						<ProjectDetailsTableView
							groupedTasks={groupedTasksForTable}
							id={id}
							setGroupedTasks={setGroupedTasksForTable}
							refetchGroups={refetchGroups}
							refetchTasks={refetchTasks}
							refetchProjectDetails={refetchProjectDetails}
							refetchProjectTags={refetchProjectTags}
							projectDetails={projectDetails}
						/>
					)}
					{activeTab === 'KANBAN_VIEW' && (
						<ProjectDetailsKanbanView
							groupedTasks={groupedTasksForKanban}
							projectId={id}
							setGroupedTasks={setGroupedTasksForKanban}
							refetchTasks={refetchTasks}
							refetchProjectTags={refetchProjectTags}
						/>
					)}
					{activeTab === 'FILES_VIEW' && (
						<div className="collection-file-section bg-gray mx-auto p-5 mt-4">
							<FileUploads
								id={id}
								originFrom="FILE"
								onSetActiveTool={() => false}
								onPlaceImageOnArtboard={() => false}
								entityType="PROJECT"
								allowedFileType="*"
							/>
						</div>
					)}
				</div>
			</section>
		</div>
	);
};

export default ProjectDetailsTaskSection;
